import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  changeEmail,
  changePassword,
  fetchProviders,
  linkTelegram,
  reauth,
  resendVerification,
  unlinkMethod,
  type LoginMethod,
  type ProvidersStatus,
  type TelegramWidgetUser,
} from '../api/auth';
import { useAuth } from '../auth/AuthContext';
import { apiErrorCode, authErrorMessage } from '../auth/errors';
import { beginGoogle } from '../auth/google';
import { beginTelegramOidc } from '../auth/telegramOidc';
import { PasswordField } from '../components/PasswordField';
import { TelegramLoginButton } from '../components/TelegramLoginButton';

type Notice = { kind: 'ok' | 'error'; text: string } | null;

export function SecurityPage() {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const { user, loading, applyUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [providers, setProviders] = useState<ProvidersStatus | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<(() => Promise<void>) | null>(null);
  const [reauthPassword, setReauthPassword] = useState('');
  const [reauthCode, setReauthCode] = useState('');
  const [needMfa, setNeedMfa] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');

  useEffect(() => {
    void fetchProviders()
      .then(setProviders)
      .catch(() => setProviders(null));
  }, []);

  useEffect(() => {
    const state = location.state as { verificationSent?: boolean; notice?: string } | null;
    if (state?.verificationSent) setNotice({ kind: 'ok', text: t('auth:verificationSentAfterRegister') });
    else if (state?.notice === 'phoneLinked') setNotice({ kind: 'ok', text: t('auth:phoneLinked') });
    else if (state?.notice === 'googleLinked') setNotice({ kind: 'ok', text: t('auth:googleLinked') });
    else if (state?.notice === 'telegramLinked') setNotice({ kind: 'ok', text: t('auth:telegramLinked') });
  }, [location.state, t]);

  /** Run a sensitive action; if the session is too old, ask for re-authentication and retry. */
  const guarded = useCallback(
    async (action: () => Promise<void>) => {
      setNotice(null);
      setBusy(true);
      try {
        await action();
      } catch (err) {
        if (apiErrorCode(err) === 'reauth_required') {
          setPending(() => action);
        } else {
          setNotice({ kind: 'error', text: authErrorMessage(t, err) });
        }
      } finally {
        setBusy(false);
      }
    },
    [t],
  );

  const onTelegramLink = useCallback(
    (tgUser: TelegramWidgetUser) =>
      void guarded(async () => {
        applyUser(await linkTelegram(tgUser));
        setNotice({ kind: 'ok', text: t('auth:telegramLinked') });
      }),
    [guarded, applyUser, t],
  );

  if (loading) {
    return (
      <div className="page">
        <div className="skeleton" style={{ minHeight: 220 }} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="page">
        <article className="profile-card">
          <h1>{t('auth:securityTitle')}</h1>
          <p>{t('auth:signInRequired')}</p>
          <Link to="/auth/login" className="btn btn-primary">
            {t('auth:backToLogin')}
          </Link>
        </article>
      </div>
    );
  }

  const methods = new Set<LoginMethod>(user.login_methods);
  const onlyOne = user.login_methods.length <= 1;

  async function onReauth(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const res = await reauth(reauthPassword, needMfa ? reauthCode : undefined);
      applyUser(res.user);
      setReauthPassword('');
      setReauthCode('');
      setNeedMfa(false);
      const retry = pending;
      setPending(null);
      if (retry) await guarded(retry);
    } catch (err) {
      if (apiErrorCode(err) === 'mfa_required') setNeedMfa(true);
      setNotice({ kind: 'error', text: authErrorMessage(t, err) });
    } finally {
      setBusy(false);
    }
  }

  async function onChangePassword(event: FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setNotice({ kind: 'error', text: t('auth:passwordsDontMatch') });
      return;
    }
    await guarded(async () => {
      const res = await changePassword(user?.has_password ? currentPassword : null, newPassword);
      applyUser(res.user);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setNotice({ kind: 'ok', text: t('auth:passwordSaved') });
    });
  }

  async function onChangeEmail(event: FormEvent) {
    event.preventDefault();
    await guarded(async () => {
      applyUser(await changeEmail(newEmail.trim(), user?.has_password ? emailPassword : null));
      setNewEmail('');
      setEmailPassword('');
      setNotice({ kind: 'ok', text: t('auth:emailSaved') });
    });
  }

  function unlink(method: 'google' | 'phone' | 'telegram') {
    void guarded(async () => {
      applyUser(await unlinkMethod(method));
      setNotice({ kind: 'ok', text: t('auth:unlinked') });
    });
  }

  const unavailable = <span className="muted-note">{t('auth:unavailableProvider')}</span>;

  return (
    <div className="page">
      <article className="profile-card security-page">
        <h1>{t('auth:securityTitle')}</h1>

        {notice ? (
          <p className={notice.kind === 'ok' ? 'auth-success' : 'auth-error'} role={notice.kind === 'ok' ? 'status' : 'alert'}>
            {notice.text}
          </p>
        ) : null}

        {pending ? (
          <section className="profile-section reauth-panel">
            <h2>{t('auth:reauthTitle')}</h2>
            {user.has_password ? (
              <form className="auth-form" onSubmit={onReauth}>
                <p className="muted-note">{t('auth:reauthIntro')}</p>
                <PasswordField
                  label={t('auth:password')}
                  value={reauthPassword}
                  onChange={setReauthPassword}
                  autoComplete="current-password"
                />
                {needMfa ? (
                  <label className="field">
                    <span>{t('auth:mfaCode')}</span>
                    <input
                      className="field-input otp-input"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={reauthCode}
                      onChange={(e) => setReauthCode(e.target.value.replace(/\D/g, ''))}
                      required
                    />
                  </label>
                ) : null}
                <div className="auth-row">
                  <button type="submit" className="btn btn-primary" disabled={busy}>
                    {t('auth:reauthCta')}
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={() => setPending(null)}>
                    {t('common:cancel', { defaultValue: 'Cancel' })}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <p className="muted-note">{t('auth:reauthPasswordless')}</p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => void logout().then(() => navigate('/auth/login'))}
                >
                  {t('auth:signInAgain')}
                </button>
              </>
            )}
          </section>
        ) : null}

        <section className="profile-section">
          <h2>{t('auth:loginMethods')}</h2>
          <div className="contact-lock-list">
            <div className="contact-lock-row">
              <span className="contact-ico" aria-hidden="true">@</span>
              <div>
                <strong>{t('auth:methodPassword')}</strong>
                <p>
                  {user.email || t('auth:notLinked')}
                  {user.email ? (
                    <span className={`status-badge ${user.email_verified ? 'is-ok' : 'is-warn'}`}>
                      {user.email_verified ? t('auth:emailVerified') : t('auth:emailNotVerified')}
                    </span>
                  ) : null}
                </p>
              </div>
              {methods.has('password') ? <span className="status-badge is-ok">{t('auth:linked')}</span> : null}
            </div>
            {user.email && !user.email_verified ? (
              <button
                type="button"
                className="btn btn-ghost btn-compact"
                disabled={busy || !providers?.email.enabled}
                onClick={() =>
                  void guarded(async () => {
                    const res = await resendVerification();
                    setNotice({
                      kind: 'ok',
                      text: res.status === 'already_verified' ? t('auth:verifyDone') : t('auth:verificationSent'),
                    });
                  })
                }
              >
                {providers?.email.enabled ? t('auth:resendVerification') : t('auth:emailDeliveryOff')}
              </button>
            ) : null}

            <div className="contact-lock-row">
              <span className="contact-ico" aria-hidden="true">G</span>
              <div>
                <strong>{t('auth:methodGoogle')}</strong>
                <p>{user.google_linked ? user.google_email || t('auth:linked') : t('auth:notLinked')}</p>
              </div>
              {user.google_linked ? (
                <button type="button" className="btn btn-ghost btn-compact" disabled={busy || onlyOne} onClick={() => unlink('google')}>
                  {t('auth:unlink')}
                </button>
              ) : providers?.google.enabled ? (
                <button
                  type="button"
                  className="btn btn-ghost btn-compact"
                  disabled={busy}
                  onClick={() => void guarded(() => beginGoogle('link'))}
                >
                  {t('auth:link')}
                </button>
              ) : (
                unavailable
              )}
            </div>

            <div className="contact-lock-row">
              <span className="contact-ico" aria-hidden="true">☎</span>
              <div>
                <strong>{t('auth:methodPhone')}</strong>
                <p>{user.phone_verified && user.phone ? user.phone : t('auth:phoneNotSet')}</p>
              </div>
              {user.phone_verified ? (
                <button type="button" className="btn btn-ghost btn-compact" disabled={busy || onlyOne} onClick={() => unlink('phone')}>
                  {t('auth:unlink')}
                </button>
              ) : providers?.sms.enabled ? (
                <Link to="/auth/phone?mode=link" className="btn btn-ghost btn-compact">
                  {t('auth:link')}
                </Link>
              ) : (
                unavailable
              )}
            </div>

            <div className="contact-lock-row">
              <span className="contact-ico" aria-hidden="true">TG</span>
              <div>
                <strong>{t('auth:methodTelegram')}</strong>
                <p>{user.telegram_id ? (user.username ? `@${user.username}` : t('auth:linked')) : t('auth:notLinked')}</p>
              </div>
              {user.telegram_id ? (
                <button type="button" className="btn btn-ghost btn-compact" disabled={busy || onlyOne} onClick={() => unlink('telegram')}>
                  {t('auth:unlink')}
                </button>
              ) : providers?.telegram.oidc_enabled ? (
                <button
                  type="button"
                  className="btn btn-ghost btn-compact"
                  disabled={busy}
                  onClick={() => void guarded(() => beginTelegramOidc('link'))}
                >
                  {t('auth:link')}
                </button>
              ) : providers?.telegram.widget_enabled && providers.telegram.bot_username ? null : (
                unavailable
              )}
            </div>
            {!user.telegram_id &&
            !providers?.telegram.oidc_enabled &&
            providers?.telegram.widget_enabled &&
            providers.telegram.bot_username ? (
              <TelegramLoginButton botUsername={providers.telegram.bot_username} onAuth={onTelegramLink} />
            ) : null}
          </div>
          {onlyOne ? <p className="muted-note">{t('auth:lastMethodHint')}</p> : null}
        </section>

        <section className="profile-section">
          <details>
            <summary>{user.has_password ? t('auth:changePasswordTitle') : t('auth:setPasswordTitle')}</summary>
            {user.email ? (
              <form className="auth-form" onSubmit={onChangePassword}>
                {user.has_password ? (
                  <PasswordField
                    label={t('auth:currentPassword')}
                    value={currentPassword}
                    onChange={setCurrentPassword}
                    autoComplete="current-password"
                  />
                ) : null}
                <PasswordField
                  label={t('auth:newPassword')}
                  value={newPassword}
                  onChange={setNewPassword}
                  autoComplete="new-password"
                  minLength={8}
                />
                <PasswordField
                  label={t('auth:confirmPassword')}
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  autoComplete="new-password"
                  minLength={8}
                  invalid={confirmPassword.length > 0 && confirmPassword !== newPassword}
                />
                <p className="muted-note">{t('auth:passwordRules')}</p>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {t('auth:savePassword')}
                </button>
              </form>
            ) : (
              <p className="muted-note">{t('auth:errors.email_required_for_password')}</p>
            )}
          </details>
        </section>

        <section className="profile-section">
          <details>
            <summary>{t('auth:changeEmailTitle')}</summary>
            <form className="auth-form" onSubmit={onChangeEmail}>
              <label className="field">
                <span>{t('auth:newEmail')}</span>
                <input
                  className="field-input"
                  type="email"
                  autoComplete="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  maxLength={255}
                  required
                />
              </label>
              {user.has_password ? (
                <PasswordField
                  label={t('auth:currentPassword')}
                  value={emailPassword}
                  onChange={setEmailPassword}
                  autoComplete="current-password"
                />
              ) : null}
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {t('auth:saveEmail')}
              </button>
            </form>
          </details>
        </section>

        <section className="profile-section">
          <h2>{t('auth:sessionsTitle')}</h2>
          <div className="auth-row">
            <button type="button" className="btn btn-ghost" onClick={() => void logout().then(() => navigate('/'))}>
              {t('auth:logout')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => void logout(true).then(() => navigate('/'))}>
              {t('auth:logoutAll')}
            </button>
          </div>
        </section>
      </article>
    </div>
  );
}
